// A NAMED "you cannot see this", never a white screen and never a silent bounce.
//
// Found 2026-08-06 while re-auditing 11 pages reported as blank. The audit's
// stated cause — "permission-gated routes rendering null" — was NOT what the
// code did: no route-level guard rendered null, and none of the 11 page
// components did either. The real mechanism was one line in `RBACGuard`, and
// the guards' *other* failure mode was this one: a silent `<Navigate to="/">`.
//
// A silent redirect is the same false-signal class as a white screen. The
// operator clicks Admin · Tenants, lands on the Dashboard, and has no way to
// tell whether the link is broken, the page is empty, or they are not allowed
// in. Saying which costs one component.
import { Link } from "react-router-dom";

import { LockIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export function AccessDenied({
  what,
  needs,
}: {
  /** The screen, in the operator's words: "tenant administration". */
  what: string;
  /** What they would need, plainly: "super-admin". Not a permission enum —
   *  an enum string tells an operator nothing they can act on. */
  needs: string;
}) {
  return (
    <Card data-testid="access-denied" className="mx-auto my-8 max-w-xl">
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <LockIcon className="h-6 w-6 text-muted-foreground" />
        <h2 className="text-base font-semibold">
          You do not have access to this page
        </h2>
        <p className="max-w-sm text-sm text-muted-foreground">
          {what} requires <strong>{needs}</strong>. Your account is signed in
          and working — this screen is simply not part of your access.
        </p>
        <Button asChild variant="outline" size="sm" className="mt-1">
          <Link to="/dashboard">Back to dashboard</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
